import React, { useCallback } from 'react';

import Add from '@mui/icons-material/Add';
import { Button, Card, FormControl, MenuItem, Select, Stack, Typography } from '@mui/material';

import { CommissionConditionEditor } from './CommissionConditionEditor';
import { OPERATOR_TEXT_MAP, TEXT_TRANSFORM_NONE } from './CommissionDialog.types';
import type {
  AddConditionHandler,
  ChangeConditionHandler,
  ChangeConditionOperatorHandler,
  CommissionGroupForm,
  ConditionIdHandler,
  GroupIdHandler,
} from './CommissionDialog.types';

interface Props {
  group: CommissionGroupForm;
  brand: string;
  addCondition: AddConditionHandler;
  handleAppendCondition: GroupIdHandler;
  handleChangeCondition: ChangeConditionHandler;
  handleConditionOperatorChange: ChangeConditionOperatorHandler;
  handleClearCondition: ConditionIdHandler;
}

export const CommissionGroupEditor = React.memo(function CommissionGroupEditor({
  group,
  brand,
  addCondition,
  handleAppendCondition,
  handleChangeCondition,
  handleConditionOperatorChange,
  handleClearCondition,
}: Props) {
  const handleAppend = useCallback(
    () => handleAppendCondition(group.operator),
    [group.operator, handleAppendCondition],
  );

  return (
    <Card variant="outlined" sx={{ borderRadius: 1, p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <FormControl size="small">
              <Select disabled value={group.operator} sx={{ minWidth: 92, borderRadius: 0.5 }}>
                <MenuItem key={group.operator} value={group.operator}>
                  {group.operator}
                </MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {OPERATOR_TEXT_MAP[group.operator]}
            </Typography>
          </Stack>
        </Stack>

        {group.conditions.map((condition) => (
          <CommissionConditionEditor
            key={condition.id}
            groupId={group.operator}
            condition={condition}
            brand={brand}
            addCondition={addCondition}
            handleChangeCondition={handleChangeCondition}
            handleConditionOperatorChange={handleConditionOperatorChange}
            handleClearCondition={handleClearCondition}
          />
        ))}

        <Stack direction="row" justifyContent="flex-start">
          <Button size="small" variant="text" sx={TEXT_TRANSFORM_NONE} onClick={handleAppend}>
            <Add /> Add condition
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
});
